import React from "react";
import { AdminLayout } from "../shared/layout";
import { useQuery } from "@tanstack/react-query";
import { getDashboardStats, getAuditLogs } from "@/data";
import { StatCard, RevenueChart, PieChart } from "@/components/admin";
import {
  Users,
  CreditCard,
  DollarSign,
  TrendingUp,
  Smartphone,
  Monitor,
  Activity,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";

export function AdminDashboardScreen() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: getDashboardStats,
  });

  const { data: activityData } = useQuery({
    queryKey: ["recent-activity"],
    queryFn: () => getAuditLogs({ page: 1, limit: 5 }),
  });

  const recentActivity = activityData?.data?.logs || [];

  const dashboardData = stats?.data;

  // MRR ve ARR büyüme hesaplama
  const mrrGrowth = dashboardData?.newSubscriptionsLast30Days || 0;
  const userGrowthPercent = dashboardData?.totalUsers
    ? ((dashboardData?.newUsersLast30Days / dashboardData?.totalUsers) * 100).toFixed(1)
    : 0;

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* İstatistik Kartları */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Users"
            value={dashboardData?.totalUsers || 0}
            change={`+${dashboardData?.newUsersLast30Days || 0} this month`}
            changeType="positive"
            icon={Users}
            isLoading={isLoading}
          />
          <StatCard
            title="Active Subscriptions"
            value={dashboardData?.activeSubscriptions || 0}
            change={`${dashboardData?.totalSubscriptions || 0} total`}
            icon={CreditCard}
            isLoading={isLoading}
          />
          <StatCard
            title="Monthly Revenue (MRR)"
            value={`$${dashboardData?.mrr || 0}`}
            change={`+${mrrGrowth} new subs`}
            changeType="positive"
            icon={DollarSign}
            isLoading={isLoading}
          />
          <StatCard
            title="Annual Revenue (ARR)"
            value={`$${dashboardData?.arr || 0}`}
            icon={TrendingUp}
            isLoading={isLoading}
          />
        </div>

        {/* Platform Dağılımı ve Gelir Grafiği */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Gelir Trendi */}
          <RevenueChart
            data={dashboardData?.revenueData || []}
            title="Revenue Trend (Last 6 Months)"
          />

          {/* Plan Dağılımı */}
          <PieChart
            data={
              dashboardData?.planDistribution?.map((item) => ({
                name: item.planName,
                value: item.count,
              })) || []
            }
            title="Subscription Plans Distribution"
            dataKey="value"
            nameKey="name"
          />
        </div>

        {/* Platform İstatistikleri */}
        {dashboardData?.platformDistribution &&
          dashboardData.platformDistribution.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Platform Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  {dashboardData.platformDistribution.map((platform, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-4 p-4 border rounded-lg"
                    >
                      {platform.lastLoginPlatform === "mobile" ? (
                        <Smartphone className="h-8 w-8 text-blue-500" />
                      ) : (
                        <Monitor className="h-8 w-8 text-green-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {platform.lastLoginPlatform || "Unknown"}
                        </p>
                        <p className="text-2xl font-bold">{platform._count}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

        {/* Son Kullanıcılar ve Abonelikler */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Son Kullanıcılar */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Users</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Platform</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData?.recentUsers?.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.name || "N/A"}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            user.status === "ACTIVE" ? "success" : "secondary"
                          }
                        >
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.lastLoginPlatform || "N/A"}
                      </TableCell>
                    </TableRow>
                  )) || (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center">
                        No recent users
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Son Abonelikler */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Subscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData?.recentSubscriptions?.map((sub) => (
              <TableRow key={sub.id}>
                <TableCell className="font-medium">
                  {sub.user?.name || sub.user?.email}
                  {sub.purchasePlatform === 'mobile' && sub.revenueCatProductId && (
                    <div className="text-xs text-blue-600 mt-1">
                      📱 RevenueCat
                    </div>
                  )}
                </TableCell>
                <TableCell>{sub.plan?.name}</TableCell>
                <TableCell>
                  ${sub.plan?.price}/{sub.plan?.interval}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      sub.status === "ACTIVE" ? "success" : "secondary"
                    }
                  >
                    {sub.status}
                  </Badge>
                  {sub.store && (
                    <div className="text-xs text-gray-500 mt-1">
                      {sub.store === 'APP_STORE' ? '🍎 App Store' : sub.store === 'PLAY_STORE' ? '🤖 Play Store' : sub.store}
                    </div>
                  )}
                </TableCell>
              </TableRow>
                  )) || (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center">
                        No recent subscriptions
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity (Audit Logs) */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity?.map((log) => (
                <div key={log.id} className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${
                      log.action.includes("CREATE") ? "bg-green-100 text-green-600" :
                      log.action.includes("UPDATE") ? "bg-blue-100 text-blue-600" :
                      log.action.includes("DELETE") ? "bg-red-100 text-red-600" :
                      "bg-gray-100 text-gray-600"
                    }`}>
                      <Activity className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {log.user?.name || "System"} <span className="text-muted-foreground">performed</span> {log.action}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        on {log.entityType} {log.entityId ? `#${log.entityId}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {format(new Date(log.createdAt), "MMM dd, HH:mm")}
                  </div>
                </div>
              )) || (
                <div className="text-center text-muted-foreground py-4">
                  No recent activity
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
